// ABOUTME: Module completion tracking store
// ABOUTME: Tracks which educational modules user has completed and quiz scores

import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import type { ModuleCompletion, QuizAttempt } from '@/types/models'
import { useGameStore } from './gameStore'

export interface ModuleState {
  completedModules: string[]
  moduleCompletions: Record<string, ModuleCompletion>
  totalXPFromModules: number
}

interface QuizAttemptInput {
  moduleId: string
  quizId: string
  score: number
  percentage: number
  passed: boolean
  timeSpent: number
}

interface ModuleActions {
  completeModule: (moduleId: string, quizScore: number) => void
  recordQuizAttempt: (input: QuizAttemptInput) => { xpAwarded: number; isFirstPass: boolean }
  hasPassedQuiz: (moduleId: string) => boolean
  getQuizHistory: (moduleId: string) => QuizAttempt[]
  getModuleCompletion: (moduleId: string) => ModuleCompletion | null
  isModuleCompleted: (moduleId: string) => boolean
  getModulesCompletedCount: () => number
  resetProgress: () => void
}

type ModuleStore = ModuleState & ModuleActions

const initialState: ModuleState = {
  completedModules: [],
  moduleCompletions: {},
  totalXPFromModules: 0,
}

export const useModuleStore = create<ModuleStore>()(
  persist(
    (set, get) => ({
      ...initialState,

      /**
       * Record a quiz attempt and determine XP award
       * Returns XP awarded (0 if retake of already-passed quiz)
       */
      recordQuizAttempt: (input: QuizAttemptInput) => {
        const { moduleId, score, percentage, passed, timeSpent } = input
        const existing = get().moduleCompletions[moduleId]
        const isFirstPass = passed && !existing?.firstPassedAt

        // Calculate XP - only award on first pass
        let xpAwarded = 0
        if (isFirstPass) {
          // Base XP
          xpAwarded = 100
          // Bonus for high scores
          if (percentage >= 90) xpAwarded += 50
          else if (percentage >= 80) xpAwarded += 30
          else if (percentage >= 70) xpAwarded += 10
        }

        // Create attempt record
        const attempt: QuizAttempt = {
          attemptNumber: (existing?.quizAttempts || 0) + 1,
          score,
          percentage,
          passed,
          timeSpent,
          completedAt: new Date(),
          xpAwarded,
        }

        // Update module completion
        const completion: ModuleCompletion = {
          moduleId,
          userId: 'local-user',
          status: passed ? 'completed' : existing?.status || 'in_progress',
          progress: passed ? 100 : existing?.progress || 0,
          quizScore: Math.max(score, existing?.quizScore || 0),
          quizAttempts: (existing?.quizAttempts || 0) + 1,
          quizHistory: [...(existing?.quizHistory || []), attempt],
          firstPassedAt: isFirstPass ? new Date() : existing?.firstPassedAt,
          startedAt: existing?.startedAt || new Date(),
          completedAt: passed ? new Date() : existing?.completedAt,
          timeSpent: (existing?.timeSpent || 0) + Math.ceil(timeSpent / 60),
        }

        set((state) => {
          const wasCompleted = state.completedModules.includes(moduleId)
          const completedModules = passed && !wasCompleted
            ? [...state.completedModules, moduleId]
            : state.completedModules

          // Track stat for achievements if this is the first completion
          if (passed && !wasCompleted) {
            useGameStore.getState().incrementModulesCompleted()
          }

          return {
            completedModules,
            moduleCompletions: {
              ...state.moduleCompletions,
              [moduleId]: completion,
            },
            totalXPFromModules: state.totalXPFromModules + xpAwarded,
          }
        })

        return { xpAwarded, isFirstPass }
      },

      /**
       * Check if quiz was previously passed (XP already awarded)
       */
      hasPassedQuiz: (moduleId: string) => {
        const completion = get().moduleCompletions[moduleId]
        return !!completion?.firstPassedAt
      },

      /**
       * Get quiz attempt history for a module
       */
      getQuizHistory: (moduleId: string) => {
        const completion = get().moduleCompletions[moduleId]
        return completion?.quizHistory || []
      },

      completeModule: (moduleId: string, quizScore: number) => {
        const existing = get().moduleCompletions[moduleId]

        // Only complete if not already completed or retaking with better score
        if (existing?.quizScore && existing.quizScore >= quizScore) {
          return // Don't update if previous score was better
        }

        const completion: ModuleCompletion = {
          moduleId,
          userId: 'local-user', // TODO: Get from auth
          status: 'completed',
          progress: 100,
          quizScore,
          quizAttempts: (existing?.quizAttempts || 0) + 1,
          startedAt: existing?.startedAt || new Date(),
          completedAt: new Date(),
          timeSpent: 0, // TODO: Track actual time spent
        }

        set((state) => {
          const wasCompleted = state.completedModules.includes(moduleId)
          const completedModules = wasCompleted
            ? state.completedModules
            : [...state.completedModules, moduleId]

          // Track stat for achievements if this is the first completion
          if (!wasCompleted) {
            useGameStore.getState().incrementModulesCompleted()
          }

          return {
            completedModules,
            moduleCompletions: {
              ...state.moduleCompletions,
              [moduleId]: completion,
            },
          }
        })
      },

      getModuleCompletion: (moduleId: string) => {
        return get().moduleCompletions[moduleId] || null
      },

      isModuleCompleted: (moduleId: string) => {
        return get().completedModules.includes(moduleId)
      },

      getModulesCompletedCount: () => {
        return get().completedModules.length
      },

      resetProgress: () => {
        set(initialState)
      },
    }),
    {
      name: 'gentleman-module-store',
      partialize: (state) => ({
        completedModules: state.completedModules,
        moduleCompletions: state.moduleCompletions,
        totalXPFromModules: state.totalXPFromModules,
      }),
    }
  )
)
